import * as React from "react";
import {
	Card,
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TablePagination,
	TableRow,
	Paper,
	IconButton,
	TableHead,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { Link } from "react-router-dom";

interface TablePaginationActionsProps {
	count: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (
		event: React.MouseEvent<HTMLButtonElement>,
		newPage: number
	) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
	const theme = useTheme();
	const { count, page, rowsPerPage, onPageChange } = props;

	const handleBackButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		onPageChange(event, page - 1);
	};

	const handleNextButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		onPageChange(event, page + 1);
	};

	return (
		<Box
			sx={ {
				flexShrink: 0,
				display: "flex",
				gap: "10px",
				padding: "14px 20px",
			} }
		>
			<IconButton
				onClick={ handleBackButtonClick }
				disabled={ page === 0 }
				aria-label="previous page"
				sx={ {
					borderRadius: "4px",
					padding: "6px",
				} }
				className="border"
			>
				{ theme.direction === "rtl" ? (
					<KeyboardArrowRight />
				) : (
					<KeyboardArrowLeft />
				) }
			</IconButton>

			<IconButton
				onClick={ handleNextButtonClick }
				disabled={ page >= Math.ceil(count / rowsPerPage) - 1 }
				aria-label="next page"
				sx={ {
					borderRadius: "4px",
					padding: "6px",
				} }
				className="border"
			>
				{ theme.direction === "rtl" ? (
					<KeyboardArrowLeft />
				) : (
					<KeyboardArrowRight />
				) }
			</IconButton>
		</Box>
	);
}

function createData(
	orderID: string,
	productImage: string,
	productName: string,
	detailsLink: string,
	date: string,
	items: number,
	price: string,
	total: string
) {
	return {
		orderID,
		productImage,
		productName,
		detailsLink,
		date,
		items,
		price,
		total,
	};
}

const rows = [
	createData(
		"#JAN-999",
		"/images/products/product1.jpg",
		"Smart Band",
		"/pos/products",
		"08 Jun 2024",
		1,
		"$35.00",
		"$2,625"
	),
	createData(
		"#JAN-1323",
		"/images/products/product2.jpg",
		"Headphone",
		"/pos/products",
		"07 Jun 2024",
		3,
		"$150.00",
		"$450.00"
	),
	createData(
		"#DEC-1234",
		"/images/products/product3.jpg",
		"iPhone 15 Plus",
		"/pos/products",
		"06 Jun 2024",
		1,
		"$$750.00",
		"$$750.00"
	),
	createData(
		"#DEC-3567",
		"/images/products/product4.jpg",
		"Bluetooth Speaker",
		"/pos/products",
		"05 Jun 2024",
		5,
		"$15.00",
		"$75.00"
	),
	createData(
		"#DEC-1098",
		"/images/products/product5.jpg",
		"Airbuds 2nd Gen",
		"/pos/products",
		"04 Jun 2024",
		2,
		"$35.00",
		"$50.00"
	),
].sort((b, a) => (a.orderID < b.orderID ? -1 : 1));

const RecentOrders: React.FC = () => {
	// Table
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(5);

	// Avoid a layout jump when reaching the last page with empty rows.
	const emptyRows =
		page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

	const handleChangePage = (
		_: React.MouseEvent<HTMLButtonElement> | null,
		newPage: number
	) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<>
			<Card
				sx={ {
					boxShadow: "none",
					borderRadius: "7px",
					mb: "25px",
					padding: { xs: "18px", sm: "20px", lg: "25px" },
				} }
				className="rmui-card"
			>
				<Box>
					<Box
						sx={ {
							display: { xs: "block", sm: "flex" },
							alignItems: "center",
							justifyContent: "space-between",
							mb: "25px",
						} }
					>
						<Typography
							variant="h3"
							sx={ {
								fontSize: { xs: "16px", md: "18px" },
								fontWeight: 700,
							} }
							className="text-black"
						>
							Recent Orders
						</Typography>

						<Typography
							variant="h3"
							sx={ {
								fontSize: { xs: "16px", md: "18px" },
								fontWeight: 700,
							} }
							className="text-black"
						>
							Customer ID : 357951
						</Typography>
					</Box>

					{/* Table */ }
					<TableContainer
						component={ Paper }
						sx={ {
							boxShadow: "none",
							borderRadius: "7px",
						} }
						className="rmui-table border"
					>
						<Table sx={ { minWidth: 650 } } aria-label="Table">
							<TableHead className="bg-f6f7f9">
								<TableRow>
									<TableCell
										sx={ {
											fontWeight: "500",
											padding: "10px 20px",
											fontSize: "14px",
										} }
										className="text-black border-bottom"
									>
										Order ID
									</TableCell>

									<TableCell
										sx={ {
											fontWeight: "500",
											padding: "10px 20px",
											fontSize: "14px",
										} }
										className="text-black border-bottom"
									>
										Product
									</TableCell>

									<TableCell
										sx={ {
											fontWeight: "500",
											padding: "10px 20px",
											fontSize: "14px",
										} }
										className="text-black border-bottom"
									>
										Items
									</TableCell>

									<TableCell
										sx={ {
											fontWeight: "500",
											padding: "10px 20px",
											fontSize: "14px",
										} }
										className="text-black border-bottom"
									>
										Price
									</TableCell>

									<TableCell
										sx={ {
											fontWeight: "500",
											padding: "10px 20px",
											fontSize: "14px",
										} }
										className="text-black border-bottom"
									>
										Total
									</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{ (rowsPerPage > 0
									? rows.slice(
										page * rowsPerPage,
										page * rowsPerPage + rowsPerPage
									)
									: rows
								).map((row) => (
									<TableRow key={ row.orderID }>
										<TableCell
											sx={ {
												padding: "14px 20px",
												fontSize: "14px",
											} }
											className="border-bottom"
										>
											{ row.orderID }
										</TableCell>

										<TableCell
											sx={ {
												padding: "14px 20px",
												fontSize: "14px",
											} }
											className="border-bottom"
										>
											<Box
												sx={ {
													display: "flex",
													alignItems: "center",
													gap: "13px",
												} }
											>
												<Box sx={ { flexShrink: "0" } }>
													<img
														src={ row.productImage }
														alt="Product"
														width={ 40 }
														height={ 40 }
														style={ { borderRadius: "7px" } }
													/>
												</Box>

												<Box>
													<Link to={ row.detailsLink }>
														<Typography
															sx={ {
																fontSize: "15px",
																fontWeight: "500",
															} }
															className="text-black"
														>
															{ row.productName }
														</Typography>
													</Link>

													<Typography
														sx={ {
															fontSize: "13px",
														} }
														className="text-body"
													>
														{ row.date }
													</Typography>
												</Box>
											</Box>
										</TableCell>

										<TableCell
											sx={ {
												padding: "14px 20px",
												fontSize: "14px",
											} }
											className="text-black border-bottom"
										>
											{ row.items }
										</TableCell>

										<TableCell
											sx={ {
												padding: "14px 20px",
												fontSize: "14px",
											} }
											className="text-black border-bottom"
										>
											{ row.price }
										</TableCell>

										<TableCell
											sx={ {
												padding: "14px 20px",
												fontSize: "14px",
											} }
											className="text-black border-bottom"
										>
											{ row.total }
										</TableCell>
									</TableRow>
								)) }
								{ emptyRows > 0 && (
									<TableRow style={ { height: 53 * emptyRows } }>
										<TableCell colSpan={ 10 } />
									</TableRow>
								) }
							</TableBody>

							<TableFooter>
								<TableRow>
									<TablePagination
										rowsPerPageOptions={ [
											5,
											10,
											25,
											{ label: "All", value: -1 },
										] }
										colSpan={ 10 }
										count={ rows.length }
										rowsPerPage={ rowsPerPage }
										page={ page }
										slotProps={ {
											select: {
												inputProps: {
													"aria-label": "rows per page",
												},
												native: true,
											},
										} }
										onPageChange={ handleChangePage }
										onRowsPerPageChange={ handleChangeRowsPerPage }
										ActionsComponent={ TablePaginationActions }
										sx={ {
											border: "none",
										} }
									/>
								</TableRow>
							</TableFooter>
						</Table>
					</TableContainer>
				</Box>
			</Card>
		</>
	);
};

export default RecentOrders;
